import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild
} from '@angular/core';
import { CellType, FlexGrid, GridPanel, Row } from '@grapecity/wijmo.grid';
import { WjFlexGridDetail } from '@grapecity/wijmo.angular2.grid.detail';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { MigrationConflictsService } from './migration-conflicts.service';
import { Conflict, ConflictResolution } from './conflict';
import { CollectionView } from '@grapecity/wijmo';
import { DetailVisibilityMode } from '@grapecity/wijmo.grid.detail';
import { debounceTime, map } from 'rxjs/operators';
import { ContactConflictService } from './contact-conflict.service';
import { TranslateService } from '@ngx-translate/core';
import { BentoAlertItemOptions } from '@bento/bento-ng';
import { ConflictStateService } from './conflict-state.service';
import {MigrationSource} from '../migration-reusable-components/migration-source.enum';

export enum ConflictResolutionCheck {
	AFTER_PAGE_LOAD_OR_REFRESH_BUTTON_CLICKED,
	AFTER_SUBMIT_RESOLVED_BUTTON_CLICKED
}

@Component({
	selector: 'app-migration-conflicts',
	templateUrl: './migration-conflicts.component.html',
	styleUrls: ['./migration-conflicts.component.css'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MigrationConflictsComponent implements OnInit, OnDestroy {
	alerts: BentoAlertItemOptions[] = [];

	@Input() migrationSource: MigrationSource; // needed for pendo tracking.

	@Input() migrationId: string;

	/**
	 * Product name to display on UI e.g NetClient CS, UltraTaxCS... (Not migration source e.g NETFIRM, ULTRATAX...)
	 * or value from competitorDescription form metadata in case of TAXCOMPETITOR migration source.
	 */
	@Input() productName: string;

	/**
	 * 1. user clicks on submit resolved button.
	 * 2. UI sends request and receives a successful response.
	 * 3. It emits the event if all conflicts from the response are resolved.
	 */
	@Output() allConflictsResolved = new EventEmitter<ConflictResolutionCheck>();

	@ViewChild('flexGrid', { static: false }) flexGrid: FlexGrid;
	@ViewChild('dp', { static: false }) detailProvider: WjFlexGridDetail;
	@ViewChild('submitResolutionButton', { static: false }) resolutionHeaderButton: ElementRef<HTMLButtonElement>;

	/**
	 * show busy loader instead of Conflict Resolution UI
	 */
	showBusyLoader = false;
	conflicts$: Observable<CollectionView<Conflict>>;
	showFooterResolutionButton$ = new BehaviorSubject(false);
	resolutionButtonDisabled$ = new BehaviorSubject(true);

	itemFormatter = this._itemFormatter.bind(this);

	scrollEvent = new Subject<void>();

	/** check only every x ms (after user stops scrolling for x ms) */
	readonly scrollCheckTimeoutMS = 200;
	/** Onvio Center Header + Menu Bar offset */
	readonly globalHeaderOffsetPx = 90;

	subscriptions = new Subscription();
	visibilityMode = DetailVisibilityMode.ExpandMulti;

	constructor(
		public migrationService: MigrationConflictsService,
		private contactConflictsService: ContactConflictService,
		private translate: TranslateService,
		private conflictStateService: ConflictStateService
	) { }

	@HostListener('window:scroll') watchScroll() {
		this.scrollEvent.next();
	}

	private _itemFormatter(panel: GridPanel, r: number, c: number, cell: HTMLElement) {
		const flex = panel.grid;
		const col = flex.columns[c];
		const row = panel.rows[r];
		// If we dont have a dataItem and its not a column header, this is a detail row
		if (!row.dataItem && panel.cellType !== CellType.ColumnHeader) {
			cell.classList.add('migration-conflict-grid-grey-cell');
		// Row header cell
		} else if (c === 0) {
			cell.classList.add('migration-conflict-row-count-column');
			cell.classList.add('migration-conflict-grid-grey-cell');
		} else if (col.binding === 'conflictStatus' && panel.cellType === CellType.Cell) {
			cell.style.paddingRight = '6px';
			cell.style.paddingLeft = '30px';
		}
	}

	ngOnInit() {
		this.showBusyLoader = true;
		this.getConflicts();
		this.subscribeToCheckIfConflictResolvedOnPageLoad();

		const scrollSub = this.scrollEvent
			.pipe(debounceTime(this.scrollCheckTimeoutMS))
			.subscribe(() => this.checkScroll());

		this.subscriptions.add(scrollSub);
	}

	private subscribeToCheckIfConflictResolvedOnPageLoad(): void {
		const subscription = this.conflicts$.subscribe((tableItems: CollectionView<Conflict>) => {
			if (this.conflictStateService.isLastConflictResolved() && this.isNotEmpty(tableItems)) {
				this.conflictStateService.setLastConflictResolved(false);
				this.showConflictHappenedMessage();
			} else if (this.isEmpty(tableItems)) {
				this.allConflictsResolved.emit(ConflictResolutionCheck.AFTER_PAGE_LOAD_OR_REFRESH_BUTTON_CLICKED);
			}
		}, () => {
			this.showSendRequestError();
		}, () => {
			this.showBusyLoader = false;
		});
		this.subscriptions.add(subscription);
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	// called when scroll happens and shows/hides footer submit button depending if header submit button is seen or not
	checkScroll() {
		// in case user starts scrolling before button is loaded
		if (this.resolutionHeaderButton == null) {
			return;
		}
		const elementRect = this.resolutionHeaderButton.nativeElement.getBoundingClientRect();
		const elementIsVisible = elementRect.top >= this.globalHeaderOffsetPx;
		this.showFooterResolutionButton$.next(!elementIsVisible);
	}

	getConflicts() {
		this.conflicts$ = this.migrationService.getList(this.migrationId, 'unresolved')
			.pipe(map(conflicts => new CollectionView(conflicts)));
	}

	// if there is any resolved conflicts we enable resolution button
	checkForResolutions(conflicts: Conflict[]): void {
		this.resolutionButtonDisabled$.next(!conflicts.some(this.isConflictResolved));
	}

	toggleDetail(row: Row) {
		const conflict = row.dataItem as Conflict;
		if (this.detailProvider.isDetailVisible(row)) {
			this.detailProvider.hideDetail(row);
		} else if (this.isConflictResolved(conflict)) {
			this.detailProvider.showDetail(row);
		} else {
			// Refresh conflict data before letting user make choice
			// TODO: Handle errors
			conflict.refreshing = true;
			this.detailProvider.showDetail(row); // show details to show busyloader
			// TODO: Currently, calling contactConflictsService is sufficient for clients/contacts/offices.
			// However, in the future, we will need to dynamically call a different
			// service for non - contact data and update this call accordingly.
			if (conflict.conflictEntityType === 'CLIENT') {
				this.contactConflictsService.updateClient(conflict.migrationId, conflict.conflictId).toPromise().then(updatedConflict => {
					this.migrationService.get(updatedConflict.migrationId, updatedConflict.conflictId).toPromise()
						.then(newConflict => this.updateRow(row, newConflict))
						.catch(this.showSendRequestError.bind(this));
				});
			} else if (conflict.conflictEntityType === 'CONTACT') {
				this.contactConflictsService.updateContact(conflict.migrationId, conflict.conflictId).toPromise().then(updatedConflict => {
					this.migrationService.get(updatedConflict.migrationId, updatedConflict.conflictId).toPromise()
						.then(newConflict => this.updateRow(row, newConflict))
						.catch(this.showSendRequestError.bind(this));
				});
			}
		}
	}

	updateRow(row: Row, newConflict: Conflict): void {
		row.dataItem.refreshing = false;
		newConflict.refreshing = false;
		const conflict = this.flexGrid.itemsSource.sourceCollection[row.dataIndex];
		this.copyValues(newConflict, conflict); // otherwise [(ngModel)] modifies old object and submit button remains disabled
		this.flexGrid.refresh();

		// workaround. for some reason wijmo doesn't refresh grid detail content even if trigger ng change detection manually
		this.detailProvider.hideDetail(row);
		this.detailProvider.showDetail(row);
	}

	private copyValues(sourceObject: any, targetObject: any): void {
		Object.keys(sourceObject).forEach(key => {
			targetObject[key] = sourceObject[key];
		});
	}

	submitResolved(conflicts: Conflict[]): void {
		this.showBusyLoader = true;
		const resolvedConflicts = conflicts
			.filter(this.isConflictResolved);

		const promises = resolvedConflicts
			.map(conflict => this.migrationService.update(conflict).toPromise());

		let errorOccurred = false;
		Promise.all(promises)
			.catch(error => {
				errorOccurred = true;
				console.error(error);
				this.showSendRequestError();
			})
			.finally(() => {
				this.getConflicts();
				this.subscribeToCheckIfConflictsResolved(errorOccurred);
			});
	}

	private subscribeToCheckIfConflictsResolved(errorOccurred: boolean): void {
		const subscription = this.conflicts$.subscribe((tableItems: CollectionView<Conflict>) => {
			if (!errorOccurred) {
				if (this.isEmpty(tableItems)) {
					this.allConflictsResolved.emit(ConflictResolutionCheck.AFTER_SUBMIT_RESOLVED_BUTTON_CLICKED);
				} else {
					const message = this.translate.instant('onvio.setup.migrations.conflict.pleaseResolve');
					this.showWarning(message);
				}
			}
		}, () => {
			this.showSendRequestError();
		}, () => {
			this.showBusyLoader = false;
		});
		this.subscriptions.add(subscription);
	}

	private showSendRequestError() {
		const errorMessage = this.translate.instant('onvio.setup.migrations.sendingError');
		this.showError(errorMessage);
	}

	private isNotEmpty(tableItems: CollectionView<Conflict>): boolean {
		return tableItems.items.length !== 0;
	}

	private isEmpty(tableItems: CollectionView<Conflict>): boolean {
		return tableItems.items.length === 0;
	}

	isConflictResolved(conflict: Conflict): boolean {
		return conflict.conflictStatus === ConflictResolution.Resolved;
	}

	showConflictHappenedMessage(): void {
		const message = this.translate.instant('onvio.setup.migrations.conflict.clientsMayAlreadyInOnvio',
			{migrationSource: this.productName});
		this.showWarning(message);
	}

	private showWarning(message: string): void {
		this.showMessage({message, type: 'warning'});
	}

	private showError(message: string): void {
		this.showMessage({message, type: 'error'});
	}

	private showMessage(messageConf: {message: string; type: string}) {
		this.alerts.push({
			type: messageConf.type,
			msg: messageConf.message,
			closeable: true
		});
	}
}
